import { zodResolver } from '@hookform/resolvers/zod'
import {
  passwordResetConfirmSchema,
  passwordResetRequestSchema,
  type PasswordResetConfirm,
  type PasswordResetRequest,
} from '@console/types'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useConfirmPasswordResetMutation,
  useRequestPasswordResetMutation,
} from '../auth/queries.js'
import { ApiError } from '../api.js'
import { AuthCard, authCardStyles as styles } from '../auth-shell/AuthCard.js'
import { Button, Input } from '../ui/index.js'

/**
 * One route, two forms — matching the single link shape the backend's email
 * builds (`${APP_BASE_URL}/reset-password?token=...`, see `backend/src/email.ts`).
 * No token in the URL means "ask for one"; a token present means "use it."
 *
 * Operator-console UX audit, presentation only. Rebuilt on `AuthCard`,
 * matching `LoginPage`'s own fix — same gap, same cause: styled beyond
 * legibility never happened for either pre-session screen.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  return token === null ? <RequestForm /> : <ConfirmForm token={token} />
}

function RequestForm() {
  const mutation = useRequestPasswordResetMutation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetRequest>({ resolver: zodResolver(passwordResetRequestSchema) })
  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values)
  })

  if (mutation.isSuccess) {
    return (
      <AuthCard documentTitle="Reset your password · Pruvato" title="Check your email">
        <p className={styles['lede']}>{mutation.data.message}</p>
        <Link to="/login" className={styles['quietAction']}>
          Back to sign in
        </Link>
      </AuthCard>
    )
  }

  return (
    <AuthCard
      documentTitle="Reset your password · Pruvato"
      title="Reset your password"
      lede="We'll send a link to reset it."
    >
      <form
        className={styles['form']}
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          {...register('email')}
          {...(errors.email && { error: errors.email.message })}
        />

        <div className={styles['actions']}>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {mutation.isPending ? 'Sending…' : 'Send reset link'}
          </Button>
          <Link to="/login" className={styles['quietAction']}>
            Back to sign in
          </Link>
        </div>

        {mutation.isError && (
          <p role="alert" className={styles['error']}>
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'That could not be sent.'}
          </p>
        )}
      </form>
    </AuthCard>
  )
}

function ConfirmForm({ token }: { token: string }) {
  const mutation = useConfirmPasswordResetMutation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetConfirm>({
    resolver: zodResolver(passwordResetConfirmSchema),
    defaultValues: { token },
  })
  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values)
  })

  if (mutation.isSuccess) {
    return (
      <AuthCard documentTitle="Reset your password · Pruvato" title="Password changed">
        <p className={styles['lede']}>{mutation.data.message}</p>
        <Link to="/login" className={styles['quietAction']}>
          Sign in
        </Link>
      </AuthCard>
    )
  }

  return (
    <AuthCard documentTitle="Reset your password · Pruvato" title="Choose a new password">
      <form
        className={styles['form']}
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <input type="hidden" {...register('token')} />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus
          {...register('newPassword')}
          {...(errors.newPassword && { error: errors.newPassword.message })}
        />

        <div className={styles['actions']}>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save new password'}
          </Button>
        </div>

        {mutation.isError && (
          <p role="alert" className={styles['error']}>
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'That could not be saved.'}
          </p>
        )}
      </form>
    </AuthCard>
  )
}
